/**
 * The link card (R7, screen 16): Link · Embed · QR code · Prefilled link as
 * one segmented control over one box, with Copy beside it.
 *
 * One component in two places: the top of Distribute › Link & access (with
 * the Prefilled tab, whose mapping rides that page's save) and, without it,
 * the "Get link…" dialog the form bar and the forms list open — the same
 * card in a modal, one click from any tab. Both used to be their own
 * implementations (DistributeDialog and PrefillLinkCard, U6/P1).
 *
 * The base URL is the account's LIVE custom domain when it has one (the
 * server folds that preference into `formLinkBaseUrl`), else the platform's
 * public origin (Platform → Settings → Public URLs), else this page's own
 * origin — the same rule the server uses for every link it mints. A
 * customer who set up forms.acme.com copies forms.acme.com links from here
 * without remembering to rewrite anything.
 *
 * The embed snippets are the one exception: see `embedBaseFor`.
 */
import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useToast } from '../../components/Toast';
import PrefillLinkCard from './PrefillLinkCard';
import type { FieldDefinition, FormAccess, FormPrefillSettings, FormWidget } from '../../shared/types';
import type { SecInfo } from './fieldGroups';

export interface DistributeTarget { id: number; name: string; slug: string }

export type LinkTabId = 'link' | 'embed' | 'qr' | 'prefill';

/**
 * Access modes a custom domain cannot complete. A single sign-on form hands
 * the respondent to the identity provider, which sends them back to the
 * callback its administrator registered — the platform origin's, the one
 * Settings › Sign-in shows. So the server moves such a form's page from
 * forms.acme.com to the platform origin as it opens (the server's
 * `platformOriginOnly` is the same list): the LINK can stay on the
 * customer's domain, the embed should not bounce inside someone's iframe.
 */
export const platformOriginOnly = (mode: FormAccess['mode'] | null | undefined): boolean => mode === 'sso';

/**
 * The origin a form's embed snippets build on: the account's link base (its
 * live custom domain, when it has one), or the platform's own origin for a
 * form whose sign-in only completes there. Null means "this page's origin".
 */
export function embedBaseFor(
  bases: { formLinkBaseUrl?: string | null; platformBaseUrl?: string | null },
  mode: FormAccess['mode'] | null | undefined,
): string | null {
  return (platformOriginOnly(mode) ? bases.platformBaseUrl : bases.formLinkBaseUrl) || null;
}

const hostOf = (url: string) => url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');

const escAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/** Every snippet the card offers, for the tests and for anything else that wants them. */
export function distributionSnippets(
  base: string, accountSlug: string, form: Pick<DistributeTarget, 'name' | 'slug'>, embedBase: string = base,
) {
  const origin = base.replace(/\/+$/, '');
  const embedOrigin = embedBase.replace(/\/+$/, '');
  const formRef = accountSlug ? `${accountSlug}/${form.slug}` : form.slug;
  const url = `${origin}/f/${formRef}`;
  const embedUrl = `${embedOrigin}/f/${formRef}`;
  const embedJs = `${embedOrigin}/embed.js`;
  const title = escAttr(form.name);
  return {
    url,
    formRef,
    iframe:
      `<iframe src="${embedUrl}" data-form-magic="${formRef}" title="${title}" loading="lazy" `
      + `style="width:100%;min-height:900px;border:0;"></iframe>\n`
      + `<script src="${embedJs}" async></script>`,
    script: `<script src="${embedJs}" data-form="${formRef}" data-title="${title}" async></script>`,
    popup: `<script src="${embedJs}" data-form="${formRef}" data-mode="popup" data-button="Open ${title}" data-title="${title}" async></script>`,
  };
}

/** What the Prefilled link tab needs: the mapping and where it saves (the Access tab's own save). */
export interface PrefillTabProps {
  formId: number;
  fields: FieldDefinition[];
  sections: SecInfo[];
  widgets?: FormWidget[];
  value: FormPrefillSettings | null;
  onChange: (next: FormPrefillSettings | null) => void;
  /** false while the mapping has unsaved edits — only the STORED mapping can be signed */
  saved: boolean;
}

export default function LinkPanel({
  form, accountSlug, publicBaseUrl, platformBaseUrl, accessMode, prefill, published = true, initial = 'link',
}: {
  form: DistributeTarget;
  accountSlug: string;
  /** the origin share links build on: the account's live custom domain, else the platform's */
  publicBaseUrl?: string | null;
  /** the platform's own public origin — where a single sign-on form's embeds load from */
  platformBaseUrl?: string | null;
  /** the form's access mode, when the caller knows it */
  accessMode?: FormAccess['mode'] | null;
  /** the Prefilled link tab; absent in the dialog */
  prefill?: PrefillTabProps;
  /** a form nobody can open yet gets a note over its link rather than no link */
  published?: boolean;
  initial?: LinkTabId;
}) {
  const [tab, setTab] = useState<LinkTabId>(initial);
  const toast = useToast();
  const base = publicBaseUrl || window.location.origin;
  const embedBase = embedBaseFor({ formLinkBaseUrl: publicBaseUrl, platformBaseUrl }, accessMode) || window.location.origin;
  const snip = distributionSnippets(base, accountSlug, form, embedBase);
  // Say why the embeds are not on the account's own domain, so nobody "fixes" them by hand.
  const embedMoved = hostOf(embedBase) !== hostOf(base);

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${what} copied.`);
    } catch {
      toast(`Could not copy — select the ${what.toLowerCase()} and copy it yourself.`, { kind: 'error' });
    }
  }

  const tabs: { id: LinkTabId; label: string }[] = [
    { id: 'link', label: 'Link' },
    { id: 'embed', label: 'Embed' },
    { id: 'qr', label: 'QR code' },
    ...(prefill ? [{ id: 'prefill' as const, label: 'Prefilled link' }] : []),
  ];

  return (
    <div className="la-linkpanel">
      <div className="fs-seg la-linktabs" role="tablist" aria-label="How to share">
        {tabs.map((t) => (
          <button
            key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''}
            onClick={() => setTab(t.id)}
          >{t.label}</button>
        ))}
      </div>
      {!published && (
        <div className="hint la-unpublished">
          Not published yet — this link opens the form once you publish. Everything here is ready to hand out before then.
        </div>
      )}
      {tab === 'link' && (
        <div className="la-linkrow">
          <div className="dd-box la-linkbox" data-testid="public-url">{snip.url}</div>
          <button className="fs-btn primary" onClick={() => { void copy(snip.url, 'Link'); }}>Copy</button>
          <a className="fs-btn neutral" href={snip.url} target="_blank" rel="noreferrer">Open ↗</a>
        </div>
      )}
      {tab === 'embed' && embedMoved && (
        <div className="hint la-platform-link" data-testid="platform-embed-note">
          This form signs people in with single sign-on, which only completes on {hostOf(embedBase)}, so its
          embeds load from there. The link and QR code stay on {hostOf(base)} and move people over as the form opens.
        </div>
      )}
      {tab === 'embed' && <EmbedTab snip={snip} onCopy={copy} />}
      {tab === 'qr' && <QrTab url={snip.url} slug={form.slug} />}
      {tab === 'prefill' && prefill && (
        <PrefillLinkCard {...prefill} publicUrl={snip.url} />
      )}
    </div>
  );
}

function QrTab({ url, slug }: { url: string; slug: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [png, setPng] = useState('');
  const [qrFailed, setQrFailed] = useState(false);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    let stale = false;
    QRCode.toCanvas(el, url, { width: 176, margin: 1 })
      .then(() => {
        if (stale) return;
        try { setPng(el.toDataURL('image/png')); } catch { /* no canvas (jsdom) — the QR still drew */ }
      })
      .catch(() => { if (!stale) setQrFailed(true); });
    return () => { stale = true; };
  }, [url]);

  return (
    <div className="dd-qr">
      <canvas ref={canvas} width={176} height={176} aria-label={`QR code for ${url}`} data-testid="qr-canvas" className="la-qr" />
      <div className="fs-stack" style={{ gap: 8 }}>
        <div className="fs-cell-sub">Points at the form's link. Print it, or put it on a slide.</div>
        {png && (
          <a className="fs-btn neutral sm" download={`${slug}-qr.png`} href={png}>Download PNG</a>
        )}
        {qrFailed && <div className="ad-err">The QR code could not be drawn.</div>}
      </div>
    </div>
  );
}

function EmbedTab({ snip, onCopy }: {
  snip: ReturnType<typeof distributionSnippets>;
  onCopy: (text: string, what: string) => void;
}) {
  const rows: [string, string, string, string][] = [
    ['Inline, sized to the container',
      'The form appears where you paste this and grows to fit its content — no scrollbar inside the page.',
      snip.script, 'Script snippet'],
    ['Pop-up button',
      'A button that opens the form over your page. Change data-button to word it.',
      snip.popup, 'Pop-up snippet'],
    ['Plain iframe',
      'For page builders that strip scripts. The second line is optional; with it the frame resizes itself.',
      snip.iframe, 'Iframe snippet'],
  ];
  return (
    <div className="fs-stack" style={{ gap: 14 }}>
      {rows.map(([title, hint, code, what]) => (
        <div key={what}>
          <div className="fs-field"><label>{title}</label></div>
          <div className="fs-cell-sub" style={{ marginBottom: 6 }}>{hint}</div>
          <div className="la-linkrow">
            <div className="dd-box la-linkbox" data-testid={`snippet-${what.split(' ')[0].toLowerCase()}`}>{code}</div>
            <button className="fs-btn neutral" onClick={() => onCopy(code, what)}>Copy</button>
          </div>
        </div>
      ))}
    </div>
  );
}
